import React from 'react';
import { Box, ButtonBase, Drawer, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { APP_BAR_HEIGHT } from '../../theme';
import { currentLink, SideNavSections, SideSection } from './SideNav';

const SIDEBAR_WIDTH = 248;

/** Shared sidebar look: sticks under the app bar and scrolls on its own when the list is long. */
export const sidebarSx = {
  width: SIDEBAR_WIDTH, flexShrink: 0, borderRight: 1, borderColor: 'divider', bgcolor: 'background.paper',
  position: 'sticky', top: APP_BAR_HEIGHT, alignSelf: 'flex-start', height: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
  overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,0,0,0.15) transparent',
  display: 'flex', flexDirection: 'column', px: 1.5, py: 2,
} as const;

/** The content column next to a sidebar: never wider than the screen, so wide tables scroll inside their own box. */
export const mainSx = {
  flexGrow: 1, minWidth: 0, px: { xs: 2, sm: 3, lg: 4 }, pt: { xs: 2, md: 3 }, pb: 6,
} as const;

export const CONTENT_MAX_WIDTH = 1480;

interface Props {
  title: string;
  icon: React.ReactNode;
  sections: SideSection[];
}

/**
 * A section of the app (investments, expenses) with its own pages: a sidebar on wide screens,
 * a bar with the current page and a menu button on narrow ones.
 */
const SectionLayout: React.FC<Props> = ({ title, icon, sections }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = React.useState(false);
  const selected = currentLink(sections, pathname);
  const selectedLabel = sections.flatMap(s => s.links).find(l => l.path === selected)?.label;

  const go = (path: string) => { navigate(path); setOpen(false); };

  const heading = (
    <ButtonBase
      onClick={() => go(sections[0].links[0].path)}
      sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1.5, p: 1, mb: 1.5, borderRadius: 3, textAlign: 'left', '&:hover': { bgcolor: 'action.hover' } }}
    >
      <Box sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: 'primary.main', color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>{title}</Typography>
    </ButtonBase>
  );

  const nav = <SideNavSections sections={sections} selected={selected} onNavigate={go} />;

  return (
    <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: `calc(100vh - ${APP_BAR_HEIGHT}px)` }}>
      {!isMobile && <Box component="nav" aria-label={title} sx={sidebarSx}>{heading}{nav}</Box>}
      <Box component="main" sx={mainSx}>
        <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: 'auto' }}>
          {isMobile && (
            <ButtonBase
              onClick={() => setOpen(true)}
              aria-label="Abrir menu da secção"
              sx={{
                width: '100%', justifyContent: 'flex-start', gap: 1, px: 1.5, py: 1, mb: 2, borderRadius: 2.5,
                border: 1, borderColor: 'divider', bgcolor: 'background.paper', textAlign: 'left',
              }}
            >
              <MenuIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" noWrap>{title}</Typography>
              {selectedLabel && <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>· {selectedLabel}</Typography>}
            </ButtonBase>
          )}
          <Outlet />
        </Box>
      </Box>
      {isMobile && (
        <Drawer open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: SIDEBAR_WIDTH + 32, px: 1.5, py: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>{heading}</Box>
            <IconButton onClick={() => setOpen(false)} aria-label="Fechar"><CloseIcon /></IconButton>
          </Box>
          {nav}
        </Drawer>
      )}
    </Box>
  );
};

export default SectionLayout;
